import { useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import type { AttentionItem } from '../types';
import { MetricCard } from '../components/common/MetricCard';
import { Card } from '../components/common/Card';
import { AttentionList } from '../components/dashboard/AttentionList';
import { CrewToday } from '../components/dashboard/CrewToday';
import { RevenueChart } from '../components/dashboard/RevenueChart';
import { ScheduleMini } from '../components/dashboard/ScheduleMini';
import { ActivityFeed } from '../components/dashboard/ActivityFeed';
import { DashboardSkeleton } from '../components/common/Skeleton';
import { SetupBeacon } from '../components/dashboard/SetupBeacon';
import { useDashboard } from '../hooks/useDashboard';
import { useAuthStore } from '../stores/authStore';
import { getBrandedName } from '../data/industryTaxonomy';
import { admin, palette } from '../styles/tokens';

export function DashboardPage() {
  const navigate = useNavigate();
  const user = useAuthStore(s => s.user);
  const business = useAuthStore(s => s.business);
  const { metrics, attentionItems, crewToday, revenueData, scheduleData, loading, error, refresh } = useDashboard(business?.id);

  const brandName = getBrandedName(business?.industry);
  useEffect(() => { document.title = `${brandName} · Dashboard`; }, [brandName]);

  const handleAttentionClick = useCallback((item: AttentionItem) => {
    switch (item.type) {
      case 'overdue_invoice': void navigate('/money/outstanding'); break;
      case 'coverage_gap': void navigate('/schedule'); break;
      case 'overtime_warning': void navigate('/team'); break;
      case 'maintenance_due': void navigate('/fleet'); break;
      case 'pending_booking': void navigate('/schedule'); break;
      default: break;
    }
  }, [navigate]);

  const now = new Date();
  const greeting = now.getHours() < 12 ? 'Good morning' : now.getHours() < 17 ? 'Good afternoon' : 'Good evening';
  const dateStr = now.toLocaleDateString('en-CA', { weekday: 'long', month: 'long', day: 'numeric' });

  if (loading) return <DashboardSkeleton />;

  if (error) {
    return (
      <div style={{ padding: '32px 20px', background: palette.redWash, borderRadius: admin.cardRadius, borderLeft: `4px solid ${palette.red}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <div><div style={{ fontSize: 14, fontWeight: 600, color: admin.header }}>Failed to load dashboard</div><div style={{ fontSize: 13, color: admin.caption, marginTop: 2 }}>{error}</div></div>
        <button onClick={refresh} style={{ padding: '7px 16px', borderRadius: 6, border: 'none', background: palette.red, color: palette.white, fontSize: 12.5, fontWeight: 600, cursor: 'pointer', fontFamily: 'inherit' }}>Try again</button>
      </div>
    );
  }

  const revenueDelta = metrics && metrics.revenueLastWeek > 0
    ? `${Math.round(((metrics.revenueThisWeek - metrics.revenueLastWeek) / metrics.revenueLastWeek) * 100)}%`
    : undefined;
  const revenueDeltaType = metrics && metrics.revenueLastWeek > 0
    ? (metrics.revenueThisWeek >= metrics.revenueLastWeek ? 'up' as const : 'down' as const)
    : undefined;

  // Build simple sparkline data from revenue chart data
  const sparkData = revenueData.length > 0 ? revenueData.map(d => d.value) : undefined;

  return (
    <div>
      <SetupBeacon />
      {/* Section 1: Greeting */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 22, fontWeight: 600, color: admin.header, letterSpacing: '-0.02em' }}>
          {greeting}, {user?.first_name ?? 'there'}
        </div>
        <div style={{ fontSize: 13, color: admin.caption, marginTop: 2 }}>
          {dateStr} &middot; Here's what needs your attention.
        </div>
      </div>

      {/* Section 2: Metric Cards (4-column grid) */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 12, marginBottom: 12 }}>
        <MetricCard
          label="Revenue this week"
          value={metrics ? `$${metrics.revenueThisWeek.toLocaleString()}` : '$0'}
          delta={revenueDelta}
          deltaType={revenueDeltaType}
          sub="vs last week"
          sparkData={sparkData}
          onClick={() => navigate('/money/revenue')}
        />
        <MetricCard
          label="Outstanding"
          value={metrics ? `$${metrics.outstanding.toLocaleString()}` : '$0'}
          valueColor={metrics && metrics.outstanding > 0 ? '#D97706' : undefined}
          delta={metrics && metrics.overdueCount > 0 ? `${metrics.overdueCount} overdue` : undefined}
          deltaType={metrics && metrics.overdueCount > 0 ? 'down' : undefined}
          sub={metrics && metrics.pendingCount > 0 ? `${metrics.pendingCount} pending` : undefined}
          onClick={() => navigate('/money/outstanding')}
        />
        <MetricCard
          label="Today's coverage"
          value={metrics ? `${metrics.coverageFilled} / ${metrics.coverageTotal}` : '0 / 0'}
          delta={metrics && metrics.coverageTotal - metrics.coverageFilled > 0
            ? `${metrics.coverageTotal - metrics.coverageFilled} gap${metrics.coverageTotal - metrics.coverageFilled > 1 ? 's' : ''}`
            : undefined}
          deltaType={metrics && metrics.coverageTotal - metrics.coverageFilled > 0 ? 'warn' : undefined}
          onClick={() => navigate('/schedule')}
        />
        <MetricCard
          label="Next payroll"
          value={metrics?.nextPayrollDate ?? '—'}
          sub={metrics && metrics.nextPayrollEstimate > 0 ? `~$${metrics.nextPayrollEstimate.toLocaleString()} estimated` : undefined}
          onClick={() => navigate('/team')}
        />
      </div>

      {/* Section 3: Two-Column Grid (Attention + Crew) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
        <Card title="Needs attention" actionLabel="View all" onAction={() => navigate('/schedule')}>
          <AttentionList items={attentionItems} onItemClick={handleAttentionClick} />
        </Card>
        <Card title="Crew today" actionLabel="Full schedule →" onAction={() => navigate('/schedule')}>
          <CrewToday crew={crewToday} />
        </Card>
      </div>

      {/* Section 4: Three-Column Grid (Revenue + Schedule + Activity) */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
        <Card title="Revenue" actionLabel="Reports →" onAction={() => navigate('/reports')}>
          <RevenueChart data={revenueData} />
        </Card>
        <Card title="Today's schedule" actionLabel="Open →" onAction={() => navigate('/schedule')}>
          <ScheduleMini data={scheduleData} />
        </Card>
        <Card title="Activity">
          <ActivityFeed businessId={business?.id} />
        </Card>
      </div>
    </div>
  );
}
